import moment, { Moment } from 'moment';
import { ComponentProps } from 'react';
import { MomentRange, MomentRanges, RangeColor, RangeIndex, rangeIndexToColor } from '../types';
import { isSameDay, isWithinRange } from '../util';
import { RangeSegment } from './RangeSegment';

interface DateRangeReference {
  range?: MomentRange;
  index: RangeIndex;
}

/**
 * Sorts a set of `DateRanges` so that the focused range is first. The returned set of
 * `DateRangeReference` objects include the `RangeIndex` so that they can be mapped
 * to the right color.
 * @param ranges A set of `DateRanges` to order.
 * @param focusedRange The `RangeIndex` of the range that is currently focused.
 * @returns An ordered set of `DateRangeReference` objects.
 */
const getOrderedRanges = (ranges: MomentRanges, focusedRange: RangeIndex) => {
  const rangeReferences = ranges.map(
    (range, i: RangeIndex): DateRangeReference => ({ range, index: i }),
  );
  // order the ranges based on which range is focused, where the focused range is first
  return focusedRange === RangeIndex.Primary ? rangeReferences : rangeReferences.reverse();
};

/**
 * Gets the ordered set of `DateRangeReference` objects that are "active" for the given
 * day, meaning that the day is within the corresponding `DateRange`.
 * @param day A day to get active ranges for.
 * @param ranges A set of `DateRanges` to check if the day is within.
 * @param focusedRange The `RangeIndex` of the range that is currently focused.
 * @returns A set of active `DateRangeReference` objects for the given day, with the
 * focused range being first.
 */
const getDayActiveRanges = (day: Moment, ranges: MomentRanges, focusedRange: RangeIndex) => {
  const orderedRanges = getOrderedRanges(ranges, focusedRange);
  // get the ranges the day is within
  return orderedRanges.filter(({ range }) => isWithinRange(day, range));
};

interface DayState {
  color: RangeColor;
  prominent: boolean;
  selected: boolean;
}

/**
 * Get the state for a given day that will determine how it is rendered.
 * @param day A day to get the state for.
 * @param ranges A set of `DateRanges` used to compute the day state.
 * @param focusedRange The `RangeIndex` of the range that is currently focused.
 * @returns The state of the day based on its location within the `DateRanges`.
 */
export const getDayState = (
  day: Moment,
  ranges: MomentRanges,
  focusedRange: RangeIndex,
): DayState => {
  const activeRanges = getDayActiveRanges(day, ranges, focusedRange);
  // the first active range determines the color/prominent state
  const activeRange = activeRanges[0];
  // if no active range then day is not selected, return default state
  if (!activeRange) return { color: 'primary', prominent: false, selected: false };
  const { range, index } = activeRange;
  return {
    color: rangeIndexToColor[index],
    prominent: isSameDay(day, range?.start) || isSameDay(day, range?.end),
    selected: true,
  };
};

interface KeyedRangeSegment extends ComponentProps<typeof RangeSegment> {
  key: RangeIndex;
}

/**
 * Given a day and a set of `DateRanges`, derives the `RangeSegment`s that should
 * be rendered for that day.
 * @param day A day from which to derive the `RangeSegment`s.
 * @param ranges A set of `DateRanges` from which to derive the `RangeSegment`s.
 * @param focusedRange The `RangeIndex` of the range that is currently focused.
 * @returns The keyed range segments to render for a given day.
 */
export const getDayRangeSegments = (
  day: Moment,
  ranges: MomentRanges,
  focusedRange: RangeIndex,
) => {
  const activeRanges = getDayActiveRanges(day, ranges, focusedRange);
  // segments are rendered to the DOM in reverse order because the last element will render on top
  return [...activeRanges].reverse().map(
    ({ range, index }): KeyedRangeSegment => ({
      key: index,
      color: rangeIndexToColor[index],
      start: range?.start === null || isSameDay(day, range?.start),
      end: range?.end === null || isSameDay(day, range?.end),
      fade: getRangeSegmentFade(day),
    }),
  );
};

/**
 * If a day is within a selected range and is the first or last day of the month, it should fade
 * in or out respectively to visualize that a range continues to the next month.
 * @param day A day from which to derive the fade.
 * @returns in, out, or undefined, depending on the intended fade behavior
 */
const getRangeSegmentFade = (day: Moment): KeyedRangeSegment['fade'] => {
  // get the start/end dates of the month the day is in
  const monthStart = moment(day).startOf('month');
  const monthEnd = moment(day).endOf('month');
  // get the day of the week; don't fade in if Sunday and don't fade out if Saturday
  const dayOfWeek = day.day();
  // day is at the beginning of the month (but not Sunday), so fade in
  if (day.isSame(monthStart, 'day') && dayOfWeek > 0) return 'in';
  // day is at the end of the month (but not Saturday), so fade out
  if (day.isSame(monthEnd, 'day') && dayOfWeek < 6) return 'out';
  // day is not at the start or end of the month so no fade is neccessary
  return undefined;
};
